import { isDefined } from "@clipboard-health/util-ts";
import constate from "constate";

import { useWorkerPreferences } from "../../../Agents/api/useWorkerPreferences";
import { DEFAULT_DISTANCE_IN_MILES_FILTER } from "../../ShiftDiscovery/Filters/constants";
import { type VisitFilters } from "./types";
import { useStoredVisitFilters } from "./useStoredVisitFilters";

interface VisitFiltersContext extends VisitFilters {
  appliedFiltersCount: number;
  clearFilters: () => void;
  setFilters: (filters: Partial<VisitFilters>) => void;
}

function useVisitFilters(): VisitFiltersContext {
  const { filters: agentPreferences, setFilters: setAgentPreferences } = useWorkerPreferences();

  const { distance = DEFAULT_DISTANCE_IN_MILES_FILTER } = agentPreferences;

  const setDistance = (value: number) => {
    setAgentPreferences({ ...agentPreferences, distance: value });
  };

  const { storedFilters, modifiedStoredFiltersCount, setStoredFilters, clearStoredFilters } =
    useStoredVisitFilters();

  const setFilters = (filters: Partial<VisitFilters>) => {
    const { distance: newDistance, ...rest } = filters;

    if (isDefined(newDistance)) {
      setDistance(newDistance);
    }

    if (Object.keys(rest).length > 0) {
      setStoredFilters((current) => ({ ...current, ...rest }));
    }
  };

  const isDistanceFilterSet = distance !== DEFAULT_DISTANCE_IN_MILES_FILTER;
  const appliedFiltersCount = modifiedStoredFiltersCount + (isDistanceFilterSet ? 1 : 0);

  const clearFilters = () => {
    setDistance(DEFAULT_DISTANCE_IN_MILES_FILTER);
    clearStoredFilters();
  };

  return {
    appliedFiltersCount,
    clearFilters,
    setFilters,
    distance,
    ...storedFilters,
  };
}

export const [VisitFiltersProvider, useVisitFiltersContext] = constate(useVisitFilters);
